/* eslint-disable css-modules/no-unused-class */
import classNames from 'classnames';
import { useState } from 'react';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import SpecialHotelBadge from '@/components/atoms/special-hotel-badge/special-hotel-badge';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import IconLabel from '@/components/molecules/icon-label/icon-label';
import PriceDisplay from '@/components/molecules/price-display/price-display';
import ProviderAnimation from '@/components/molecules/provider-animation/provider-animation';
import StarRating from '@/components/molecules/star-rating/star-rating';
import Tag from '@/components/molecules/tag/tag';
import FavoriteHeart from '@/components/organisms/favorite-heart/favorite-heart';
import FeatureList from '@/components/organisms/feature-list/feature-list';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import { SliderImageItem } from '@/components/organisms/image-slider/image-slider';
import LogoFeatureList from '@/components/organisms/logo-feature-list/logo-feature-list';
import PriceDetails from '@/components/organisms/price-details/price-details';
import variables from '@/components/organisms/product-card/product-card.variables';
import TagList from '@/components/organisms/tag-list/tag-list';
import TripSummary from '@/components/organisms/trip-summary/trip-summary';
import { HubspotFormData } from '@/components/templates/search/search-page-results/search-page-results';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './product-card.module.scss';

export type ProductCardVariant = 'primary' | 'secondary' | 'tertiary';

const INTERACTION_THRESHOLD = 2;

export interface ProductCardProps {
  /** An additional tag to display on the card */
  additionalTag?: Content.Components['ProductCardDto']['additionalTag'];
  /** The class name to apply */
  className?: string;
  /** Indicates whether to use a compact version */
  compact?: boolean;
  /** Departure name to display */
  departureName?: string;
  /** Text to show as description */
  description?: Content.Components['ProductCardDto']['description'];
  /** Duration information to display */
  duration?: string;
  /** The information about the scheduled flights */
  highlightScheduled?: Content.Components['HighlightScheduledDto'];
  /** Homebound departure date to display */
  homeboundDepartureDate?: string;
  /** Homebound departure name to display */
  homeboundDepartureName?: string;
  /** Hotel identifier */
  hotelId?: number;
  /** Hotel name to display */
  hotelName?: string;
  /** The info to pass to the hubspot */
  hubspotInfo?: HubspotFormData;
  /** Tag array to display within the image slider */
  imageTags?: string[];
  /** Image array to display in the slider */
  images?: SliderImageItem[];
  /** Features list to display */
  includedProducts?: Content.Components['ProductCardDto']['includedProducts'];
  /** Indicates whether the card is favorite */
  isFavorite?: boolean;
  /** Indicates whether the product is a leisure card */
  isLeisureCard?: boolean;
  /** Indicates whether to display the loading state */
  isLoading?: boolean;
  /** Indicates whether the product is recommended */
  isRecommended?: boolean;
  /** Indicates whether the product is sold out */
  isSoldOut?: boolean;
  /** Location to display */
  location?: string | null;
  /** The type of the middle section data */
  middleSection?: Content.Components['ProductCardDto']['middleSection'];
  /** Offer amount to display */
  offerAmount?: string;
  /** Card identifier */
  offerId?: Content.Components['ProductCardDto']['offerId'];
  /** Offer tag text to display */
  offerTag?: string;
  /** An event that is triggered on product card click */
  onClick?: () => void;
  /** An event that is triggered on click favorite */
  onClickFavorite?: () => void;
  /** An event that is triggered on click inquiry */
  onClickInquiry?: (value?: HubspotFormData) => void;
  /** An event that is triggered to fetch more images */
  onFetchMoreImages?: () => void;
  /** Outbound arrival date to display */
  outboundArrivalDate?: string;
  /** Outbound departure date to display */
  outboundDepartureDate?: string;
  /** Outbound departure name to display */
  outboundDepartureName?: string;
  /** Price to display */
  price?: string;
  /** Price breakdown */
  priceDetails?: Content.Components['PriceDetailsDto'];
  /** Price discount to display */
  priceDiscount?: string;
  /** Price label to display */
  priceLabel?: string;
  /** Indicates the product card type */
  productCardType?: Content.Components['ProductCardDto']['productCardType'];
  /** Indicates the provider name of the offer */
  providerCode?: TravelPortal.Components['TravelPortalOffer']['brandTag'];
  /** Rating to display in the star rating*/
  rating?: number;
  /** Indicates whether to display the heartHotel logo */
  showHeartHotelLogo?: boolean;
  /** Indicates whether image overlays are shown on the first slide */
  showImageOverlaysOnFirstSlide?: boolean;
  /** The special hotel type to show */
  specialHotelType?: Content.Components['SpecialHotelType'];
  /** Tag array to display within the card */
  tags?: string[];
  /** The theme of the parent module */
  theme?: Theme;
  /** Transport mode for the product card */
  transportMode?: Content.Components['ProductCardDto']['transportMode'];
  /** The trip highlights items to display */
  tripHighlights?: Content.Components['ProductCardDto']['tripHighlights'];
  /** The trip summary items to display */
  tripSummary?: Content.Components['ProductCardDto']['tripSummary'];
  /** The unique id for each offer */
  uniqueId?: string;
  /** The urgency tags to show */
  urgencyTags?: Content.Components['ProductCardDto']['urgencyTags'];
  /** The url to link to */
  url?: string;
  /** The variant of the product card */
  variant?: ProductCardVariant;
}

const variants: Record<ProductCardVariant, string> = {
  primary: styles.isPrimary,
  secondary: styles.isSecondary,
  tertiary: styles.isTertiary,
};

const transportIcons: Record<
  Exclude<Content.Components['ProductCardDto']['transportMode'], null>,
  [IconName, IconName]
> = {
  bus: ['Bus', 'Bus'],
  own: ['Car', 'Car'],
  plane: ['PlaneTakeOff', 'PlaneLanding'],
  train: ['Train', 'Train'],
  undefined: ['Bus', 'Bus'], // Provide a default icon pair for undefined
};

const ProductCard = ({
  additionalTag,
  className,
  compact = false,
  departureName,
  description,
  duration,
  highlightScheduled,
  homeboundDepartureDate,
  homeboundDepartureName,
  hotelName,
  hubspotInfo,
  images = [],
  imageTags = [],
  includedProducts = [],
  isFavorite = false,
  isLeisureCard = false,
  isLoading = false,
  isRecommended = false,
  isSoldOut = false,
  location,
  middleSection = 'facilityTags',
  offerAmount,
  offerTag,
  onClick,
  onClickFavorite,
  onClickInquiry,
  onFetchMoreImages,
  outboundArrivalDate,
  outboundDepartureDate,
  outboundDepartureName,
  price,
  priceDetails,
  priceDiscount,
  priceLabel,
  productCardType = 'default',
  providerCode,
  rating = 0,
  showHeartHotelLogo = false,
  showImageOverlaysOnFirstSlide = false,
  specialHotelType,
  tags = [],
  theme,
  transportMode,
  tripHighlights,
  tripSummary,
  urgencyTags = [],
  url,
  variant = 'primary',
}: ProductCardProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const isTablet = useWindowSize('tablet');
  const { bindRef, isHovered } = useIsHovered<HTMLAnchorElement>();
  const [isSwiping, setIsSwiping] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { dateFormatter } = useDateFormatter();
  const dateFormat = 'ddd DD-MM-YY';
  const dateTimeFormat = 'ddd DD-MM-YY, HH:mm';

  let outbound: string | undefined,
    homebound: string | undefined,
    outboundDate: string | undefined,
    homeboundDate: string | undefined;

  switch (transportMode) {
    case 'own':
      {
        if (compact) {
          outbound = `${t('items.common.transportMode.car')}, ${duration}, `;
          outboundDate = `${dateFormatter(outboundArrivalDate).format(dateFormat)}`;

          break;
        }

        outbound = `${t('items.search.productCard.arrival')}, `;
        homebound = `${t('items.search.productCard.homeJourney')}, `;

        outboundDate = `${dateFormatter(outboundArrivalDate).format(dateFormat)}`;
        homeboundDate = `${dateFormatter(homeboundDepartureDate).format(dateFormat)}`;
      }
      break;

    case 'bus':
      {
        // For bus there is no "outboundDepartureName" or "homeboundDepartureName" as there is multiple pickup points.
        if (compact) {
          outbound = `${departureName}, ${duration}, `;
          outboundDate = `${dateFormatter(outboundDepartureDate).format(dateFormat)}`;

          break;
        }

        outbound = `${t('items.search.productCard.departure')}, ${departureName}, `;
        homebound = `${t('items.search.productCard.homeJourney')}, ${departureName},`;

        outboundDate = `${dateFormatter(outboundDepartureDate).format(dateFormat)}`;
        homeboundDate = `${dateFormatter(homeboundDepartureDate).format(dateFormat)}`;
      }
      break;

    case 'train':
      {
        // For train there is no "outboundDepartureName" or "homeboundDepartureName" as there is multiple pickup points.
        if (compact) {
          outbound = `${departureName}, ${duration}, `;
          outboundDate = `${dateFormatter(outboundDepartureDate).format(dateFormat)}`;

          break;
        }

        outbound = `${t('items.search.productCard.departure')}, ${departureName}, `;
        homebound = `${t('items.search.productCard.homeJourney')}, ${departureName},`;

        outboundDate = `${dateFormatter(outboundDepartureDate).format(dateFormat)}`;
        homeboundDate = `${dateFormatter(homeboundDepartureDate).format(dateFormat)}`;
      }
      break;

    case 'plane':
      {
        if (compact) {
          outbound = `${t('items.common.transportMode.plane')} ${outboundDepartureName}, ${duration}, `;
          outboundDate = `${dateFormatter(outboundDepartureDate).format(dateTimeFormat)}`;

          break;
        }

        outbound = `${t('items.common.transportMode.plane')} ${outboundDepartureName}, `;
        homebound = `${t('items.common.transportMode.plane')} ${homeboundDepartureName}, `;

        outboundDate = `${dateFormatter(outboundDepartureDate).format(dateTimeFormat)}`;
        homeboundDate = `${dateFormatter(homeboundDepartureDate).format(dateTimeFormat)}`;
      }
      break;
  }

  const getSenseOfUrgency = (type: Content.Components['UrgencyTagType']) => {
    const tag = urgencyTags?.find(tag => tag.tagType === type);

    if (!tag) return null;

    if (type === 'roomsLeft') {
      return <Tag text={tag.text} variant="primary" />;
    }

    if (type === 'dailyViews') {
      return (
        <div className={styles.dailyViews}>
          <Icon name="Eye" size={16} />

          <Typography tagStyle="bodySmall">{tag.text}</Typography>
        </div>
      );
    }
  };

  const hasImageTags = imageTags.length > 0;
  const shouldShowBrandOrTags = variant !== 'secondary' || !isLaptop;
  const shouldShowImageTags =
    shouldShowBrandOrTags && !providerCode && hasImageTags;

  const imageOverlays = (
    <React.Fragment>
      {/* !NOTE: currently heart hotel is separate from the special hotel types so we need to handle it separately
          When BE aligns we can remove the special case and align all the special types */}
      {showHeartHotelLogo ? (
        <SpecialHotelBadge
          className={styles.specialHotelBadge}
          type="HeartHotel"
        />
      ) : (
        specialHotelType && (
          <SpecialHotelBadge
            className={styles.specialHotelBadge}
            type={specialHotelType}
          />
        )
      )}

      {shouldShowImageTags && (
        <TagList
          className={styles.imageTagList}
          tags={imageTags.map(imageTag => ({
            text: imageTag,
            variant: 'primary',
          }))}
        />
      )}
    </React.Fragment>
  );

  const renderMiddleSectionContent = () => {
    if (compact || variant !== 'primary') return null;

    if (middleSection === 'facilityTags' && tags.length > 0) {
      return (
        <TagList
          className={styles.tagList}
          hasSideScroll={!isTablet}
          tags={tags.map(tag => ({
            text: tag,
            variant: 'secondary',
          }))}
        />
      );
    }

    if (middleSection === 'description' && description) {
      return (
        <Typography className={styles.description} tagStyle="bodyMedium">
          {description}
        </Typography>
      );
    }
  };

  const renderIconLabel = (
    icon: IconName,
    label: string | undefined,
    date: string | undefined,
  ) => {
    if (!label) return null;

    return (
      <IconLabel
        icon={icon}
        isInProductCard
        secondLineText={date}
        text={label}
      />
    );
  };

  if (isLoading) {
    return (
      <div className={classNames(styles.component, className)}>
        <div
          className={classNames(
            styles.cardWrapper,
            styles.isLoading,
            variants[variant],
          )}
        >
          <div className={styles.contentWrapper}>
            <div className={styles.imageWrapper}>
              <SkeletonLoader height="100%" width="100%" />
            </div>

            <div className={styles.hotelDetailsWrapper}>
              <div className={styles.hotelDetails}>
                <SkeletonLoader borderRadius="4px" height="20px" width="70%" />

                <SkeletonLoader borderRadius="4px" height="16px" width="50%" />

                <SkeletonLoader borderRadius="4px" height="20px" width="100%" />
              </div>

              <div className={styles.featuresDetails}>
                <SkeletonLoader borderRadius="4px" height="16px" width="80%" />

                <SkeletonLoader borderRadius="4px" height="16px" width="60%" />
              </div>
            </div>
          </div>

          {isLeisureCard && (
            <div className={styles.tripSummaryWrapper}>
              <SkeletonLoader borderRadius="16px" height="50px" width="100%" />
            </div>
          )}
        </div>
      </div>
    );
  }

  const renderContent = () => (
    <React.Fragment>
      <div className={styles.contentWrapper}>
        <div className={styles.imageWrapper}>
          {priceDiscount && priceDiscount !== '0' && !isSoldOut && (
            <PriceDisplay
              additionalTag={additionalTag?.text}
              additionalTagVariant={additionalTag?.theme}
              className={styles.priceDisplay}
              primaryHeadline={priceDiscount}
              variant="secondary"
            />
          )}

          <ImageSlider
            compact={compact}
            firstSlideOverlay={
              showImageOverlaysOnFirstSlide ? imageOverlays : null
            }
            images={images}
            interactionThreshold={INTERACTION_THRESHOLD}
            onFetchMoreImages={onFetchMoreImages}
            onTouchEnd={() => setIsSwiping(false)}
            onTouchStart={() => setIsSwiping(true)}
            showNavigationOnBottom={variant === 'secondary' && isLaptop}
          />

          {!showImageOverlaysOnFirstSlide && imageOverlays}

          {shouldShowBrandOrTags && providerCode && (
            <div className={styles.brandTag}>
              <ProviderAnimation
                isHovered={isHovered}
                providerCode={providerCode}
              />
            </div>
          )}
        </div>

        <div className={styles.hotelDetailsWrapper}>
          <div className={styles.hotelDetails}>
            <div className={styles.top}>
              <div className={styles.hotelNameWrapper}>
                <Typography
                  className={styles.hotelName}
                  tag="h3"
                  tagStyle="headingSmall"
                >
                  {hotelName}
                </Typography>

                {!isSoldOut && onClickFavorite && (
                  <FavoriteHeart
                    isFavorite={isFavorite}
                    onClickFavorite={() => onClickFavorite?.()}
                  />
                )}
              </div>

              {location && <IconLabel icon="Location" text={location} />}

              {productCardType === 'default' && (
                <React.Fragment>
                  <StarRating rating={rating} />

                  {renderMiddleSectionContent()}
                </React.Fragment>
              )}
            </div>

            <div className={styles.bottom}>
              {productCardType !== 'default' ? (
                <div className={styles.featuresDetails}>
                  <Typography
                    className={styles.featuresHeading}
                    tag="label"
                    tagStyle="bodyMediumBold"
                  >
                    {t('items.search.productCard.journeyHighlights')}
                  </Typography>

                  {tripHighlights && (
                    <LogoFeatureList features={tripHighlights} />
                  )}
                </div>
              ) : (
                includedProducts.length > 0 && (
                  <div className={styles.featuresDetails}>
                    {!compact && (
                      <Typography
                        className={styles.featuresHeading}
                        tag="label"
                        tagStyle="bodyMediumBold"
                      >
                        {t('items.search.productCard.includedInTrip')}
                      </Typography>
                    )}

                    <FeatureList features={includedProducts} />
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        <div
          className={classNames(styles.priceSectionWrapper, {
            [styles.isScheduled]: !!highlightScheduled,
          })}
        >
          {!!highlightScheduled && (
            <div className={styles.scheduledDetails}>
              <Typography tagStyle="labelMedium">
                {highlightScheduled.text}

                {!!highlightScheduled.link && (
                  <Link
                    className={styles.link}
                    onClick={event => {
                      event?.preventDefault();
                      event?.stopPropagation();
                    }}
                    url={highlightScheduled.link.url}
                  >
                    <Typography tagStyle="labelMedium">
                      {highlightScheduled.link.text}
                    </Typography>
                  </Link>
                )}
              </Typography>
            </div>
          )}

          <div className={styles.tripDetails}>
            {transportMode && transportIcons[transportMode] && (
              <React.Fragment>
                {renderIconLabel(
                  transportIcons[transportMode][0],
                  outbound,
                  outboundDate,
                )}

                {renderIconLabel(
                  transportIcons[transportMode][1],
                  homebound,
                  homeboundDate,
                )}
              </React.Fragment>
            )}

            {duration && !compact && (
              <IconLabel icon="Calendar" isInProductCard text={duration} />
            )}
          </div>

          <div className={styles.priceDetails}>
            {isSoldOut ? (
              <React.Fragment>
                <Typography
                  className={styles.price}
                  tag="label"
                  tagStyle="priceLarge"
                >
                  {t('items.common.words.soldOut')}
                </Typography>

                <div className={styles.priceAndButton}>
                  <Button
                    className={styles.secondaryButton}
                    onClick={event => {
                      event?.preventDefault();
                      event?.stopPropagation();
                      onClickInquiry?.(hubspotInfo);
                    }}
                    text={t('items.common.words.inquire')}
                    variant="secondary"
                  />

                  <Button
                    aria-label={t('items.common.words.seeOffer')}
                    hovered={isHovered}
                    icon="ChevronRight"
                    variant="primary"
                  />
                </div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {getSenseOfUrgency('dailyViews')}

                <button
                  className={styles.iconButton}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsDetailsOpen(true);
                  }}
                  type="button"
                >
                  <Typography
                    className={styles.priceHeading}
                    tag="label"
                    tagStyle="bodyMedium"
                  >
                    {priceLabel}

                    <Icon name="Info" size={16} />
                  </Typography>
                </button>

                <div className={styles.priceAndButton}>
                  <div className={styles.priceAndOffer}>
                    {(!!offerAmount || !!urgencyTags?.length) && (
                      <div className={styles.offerWrapper}>
                        {!!urgencyTags?.length &&
                          getSenseOfUrgency('roomsLeft')}

                        {!!offerAmount && (
                          <React.Fragment>
                            <Tag
                              text={
                                offerTag
                                  ? offerTag
                                  : t('items.search.productCard.offer')
                              }
                              variant="tertiary"
                            />

                            <Typography
                              className={styles.offerAmount}
                              tag="label"
                              tagStyle="bodyMedium"
                            >
                              {offerAmount}
                            </Typography>
                          </React.Fragment>
                        )}
                      </div>
                    )}

                    <Typography
                      className={styles.price}
                      tag="label"
                      tagStyle="priceLarge"
                    >
                      {price}
                    </Typography>
                  </div>

                  <Button
                    aria-label={t('items.common.words.seeOffer')}
                    className={styles.seeOffer}
                    hovered={isHovered}
                    icon={variables.cta.icon as IconName}
                    variant={variables.cta.variant as ProductCardVariant}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>

      {productCardType === 'multiDestination' && (
        <div className={styles.tripSummaryWrapper}>
          <TripSummary tripSummaryItems={tripSummary} />
        </div>
      )}
    </React.Fragment>
  );

  const renderCard = () => (
    <Link
      ref={bindRef}
      className={classNames(
        styles.cardWrapper,
        theme === 'dark' && variables?.settings?.cardTheme
          ? styles[variables.settings.cardTheme]
          : undefined,
        {
          [styles.isCompact]: compact,
          [styles.isRecommended]: isRecommended,
          [styles.isSwiping]: isSwiping,
          [styles.isSoldOut]: isSoldOut,
        },
        variants[variant],
      )}
      onClick={onClick}
      prefetch={false}
      url={url}
    >
      {renderContent()}
    </Link>
  );

  return (
    <div className={classNames(styles.component, className)}>
      {isRecommended ? (
        <div className={styles.isRecommendedWrapper}>
          <div className={styles.isRecommendedContent}>
            <div className={styles.isRecommendedLeftSection}>
              <div className={styles.recommendedIconWrapper}>
                <Icon name="Sparkles" size={24} />

                <Typography tagStyle="headingSmall">
                  {t('items.search.productCard.recommendedHeadline')}
                </Typography>
              </div>

              <Typography
                className={styles.recommendedText}
                tagStyle="bodyMedium"
              >
                {t('items.search.productCard.recommendedText')}
              </Typography>
            </div>

            <Tag
              text={t('items.search.productCard.recommendedTag')}
              variant="quinary"
            />
          </div>

          {renderCard()}
        </div>
      ) : (
        renderCard()
      )}

      {priceDetails && (
        <PriceDetails
          isOpen={isDetailsOpen}
          priceDetails={priceDetails}
          setIsOpen={setIsDetailsOpen}
          url={priceDetails.searchUrl}
        />
      )}
    </div>
  );
};

ProductCard.displayName = 'ProductCard';

export default ProductCard;
